<template>
  <div>
    <QuickMoveInMapFilters
      :prices="prices"
      :beds="beds"
      :baths="baths"
      :neighborhoods="neighborhoods"
      :results-count="quickMoveIns.length"
    />

    <div class="list" :class="{ 'is-full': !isMapDisplayed, 'is-full-triggered': !isMapDisplayed }">
      <QuickMoveInListDrawer />
      <MapboxMap
        class="map"
        v-if="isMapDisplayed"
        :access-token="accessToken"
        :map-style="mapStyle"
        :center="coordinates"
        :zoom="9"
        @mb-created="(mapboxInstance) => (map = mapboxInstance)"
      >
        <button type="button" class="map-close" @click="closeMap">
          <IconClose />
        </button>
        <div class="map-poi">
          <ToggleButton
            class="map-poi-switch"
            :color="{ checked: '#000000', unchecked: '#9F8054', disabled: '#CCCCCC' }"
            :width="34"
            :height="22"
            @change="togglePoi"
          />
          Points of Interest
        </div>

        <template v-for="c in quickMoveInsWithCoordinates">
          <MapboxMarker
            v-if="c.coordinates"
            :lng-lat="c.coordinates"
            :ref="`mapbox-marker-${c.id}`"
            :key="`mapbox-marker-${c.id}`"
          >
            <div class="marker-icon"></div>
          </MapboxMarker>
          <MapboxPopup
            v-if="c.coordinates"
            :lng-lat="c.coordinates"
            :close-button="false"
            :close-on-click="false"
            :offset="12"
            :className="`mapbox-popup-${c.id}`"
            :ref="`mapbox-popup-${c.id}`"
            :key="`mapbox-popup-${c.id}`"
          >
            <div class="popup-thumbnail">
              <img :src="c.slide_images[0].url" :alt="c.slide_images[0].title" />
            </div>
            <div class="popup-price">${{ c.price }}+</div>
            <div class="popup-title">{{ c.title }}</div>
          </MapboxPopup>
        </template>

        <template v-if="showPointsOfInterest">
          <MapboxMarker
            v-for="poi in pointsOfInterest"
            :lng-lat="[poi.address.lng, poi.address.lat]"
            :ref="`mapbox-marker-poi-${poi.id}`"
            :key="`mapbox-marker-poi-${poi.id}`"
            :popup="{
              lngLat: [poi.address.lng, poi.address.lat],
              closeButton: false,
              closeOnClick: true,
              offset: 12,
              className: `mapbox-popup-${poi.id}`,
            }"
          >
            <component :is="`icon-poi-${poi.poi_type.value}`" />
            <template v-slot:popup>
              <div class="popup-title">{{ poi.title }}</div>
            </template>
          </MapboxMarker>
        </template>

        <MapboxNavigationControl position="bottom-right" />
      </MapboxMap>
    </div>
  </div>
</template>

<script>
import * as _ from 'lodash';
import geocodingClient from '@mapbox/mapbox-sdk/services/geocoding';
import MapboxMap from '@studiometa/vue-mapbox-gl/dist/components/MapboxMap';
import MapboxMarker from '@studiometa/vue-mapbox-gl/dist/components/MapboxMarker';
import MapboxNavigationControl from '@studiometa/vue-mapbox-gl/dist/components/MapboxNavigationControl';
import MapboxPopup from '@studiometa/vue-mapbox-gl/dist/components/MapboxPopup';
import { ToggleButton } from 'vue-js-toggle-button';

import IconClose from '@/components/icons/IconClose';
import IconPoiGas from '@/components/icons/IconPoiGas';
import IconPoiCoffee from '@/components/icons/IconPoiCoffee';
import IconPoiDaycare from '@/components/icons/IconPoiDaycare';
import IconPoiDogPark from '@/components/icons/IconPoiDogPark';
import IconPoiEducation from '@/components/icons/IconPoiEducation';
import IconPoiGrocery from '@/components/icons/IconPoiGrocery';
import IconPoiGym from '@/components/icons/IconPoiGym';
import IconPoiMedical from '@/components/icons/IconPoiMedical';
import IconPoiPark from '@/components/icons/IconPoiPark';
import IconPoiRestaurant from '@/components/icons/IconPoiRestaurant';
import IconPoiShopping from '@/components/icons/IconPoiShopping';
import IconPoiAirport from '@/components/icons/IconPoiAirport';
import IconPoiEntertainment from '@/components/icons/IconPoiEntertainment';
import IconPoiGolf from '@/components/icons/IconPoiGolf';
import IconPoiWinery from '@/components/icons/IconPoiWinery';
import IconPoiTrain from '@/components/icons/IconPoiTrain';
import IconPoiCourthouse from '@/components/icons/IconPoiCourthouse';
import QuickMoveInMapFilters from '@/components/filters/QuickMoveInMapFilters';
import QuickMoveInListDrawer from '@/components/QuickMoveInListDrawer';

export default {
  name: 'QuickMoveInsMap',
  components: {
    QuickMoveInListDrawer,
    QuickMoveInMapFilters,
    IconClose,
    IconPoiCoffee,
    IconPoiDaycare,
    IconPoiDogPark,
    IconPoiEducation,
    IconPoiGas,
    IconPoiGrocery,
    IconPoiGym,
    IconPoiMedical,
    IconPoiPark,
    IconPoiRestaurant,
    IconPoiShopping,
    IconPoiAirport,
    IconPoiEntertainment,
    IconPoiGolf,
    IconPoiWinery,
    IconPoiTrain,
    IconPoiCourthouse,
    MapboxMap,
    MapboxMarker,
    MapboxNavigationControl,
    MapboxPopup,
    ToggleButton,
  },
  props: {
    city: {
      required: true,
      type: String,
    },
  },
  data() {
    const accessToken = window.atmoMaps.mapbox_api_key;
    const mapboxGeoCodingClient = geocodingClient({ accessToken });

    const cityCoordinates = {
      pittsburgh: [-79.9959, 40.4406],
      philadelphia: [-75.1652, 39.9526],
    };

    return {
      prices: [300000, 400000, 500000, 600000, 700000, 800000, 900000, 1000000, 1100000, 1200000],
      baths: [
        {
          label: 'Any',
          value: 'any',
        },
        {
          label: '1+',
          value: '1',
        },
        {
          label: '2+',
          value: '2',
        },
        {
          label: '3+',
          value: '3',
        },
        {
          label: '4+',
          value: '4',
        },
        {
          label: '5+',
          value: '5',
        },
      ],
      beds: [
        {
          label: 'Any',
          value: 'any',
        },
        {
          label: '1+',
          value: '1',
        },
        {
          label: '2+',
          value: '2',
        },
        {
          label: '3+',
          value: '3',
        },
        {
          label: '4+',
          value: '4',
        },
        {
          label: '5+',
          value: '5',
        },
      ],
      accessToken,
      mapboxGeoCodingClient,
      mapStyle: 'mapbox://styles/atmo/cl7s4bi4d004414uq9rbkbf3x',
      map: null,
      popupObserver: null,
      coordinates: cityCoordinates[this.city],
      showPointsOfInterest: false,
      pointsOfInterest: [],
    };
  },
  computed: {
    communities() {
      // console.log('QuickMoveInsMap communities()');
      return this.$store.getters['communities/getCommunitiesAll'];
    },
    neighborhoods() {
      return this.communities.map((c) => c.title).sort();
    },
    isDetailedView() {
      return this.$store.getters['ui/getPanelView'] === 'detail';
    },
    isMapDisplayed() {
      return this.$store.getters['ui/isMapDisplayed'];
    },
    quickMoveIn() {
      return this.$store.getters['ui/getDetailedQuickMoveIn'];
    },
    quickMoveIns() {
      return this.$store.getters['quickMoveIns/getQuickMoveIns'];
    },
    quickMoveInsWithCoordinates() {
      // console.log('QuickMoveInsMap quickMoveInsWithCoordinates() filter');
      return this.quickMoveIns.filter((quickMoveIn) => {
        return Array.isArray(quickMoveIn.coordinates);
      });
    },
  },
  watch: {
    map: function(newValue) {
      // Set up the MutationObserver whenever the map instance is assigned or replaced
      if (this.popupObserver) this.popupObserver.disconnect();
      if (newValue) this.setupPopupObserver();
    },
    quickMoveIn: function(newValue, oldValue) {
      // console.log('QuickMoveInsMap watch quickMoveIn', newValue, oldValue, this.isDetailedView);
      if (oldValue !== false && this.isDetailedView === true) {
        this.showPopupDetail();
      }
    },
    quickMoveIns: function(newValue, oldValue) {
      // console.log('QuickMoveInsMap watch quickMoveIns', newValue.length, oldValue.length);
      if (newValue.length > oldValue.length) {
        this.updateQuickMoveInsCoordinates();
      }
    },
    isDetailedView: function(newValue, oldValue) {
      // console.log('QuickMoveInsMap watch isDetailedView', newValue, oldValue);
      if (oldValue === false && newValue === true) {
        this.showPopupDetail();
      } else {
        this.clearPopups();
      }
    },
  },
  mounted() {
    this.fetchData();
  },
  beforeDestroy() {
    if (this.popupObserver) this.popupObserver.disconnect();
  },
  methods: {
    clearPopups() {
      const popups = document.getElementsByClassName('mapboxgl-popup');
      popups.forEach((el) => {
        // dim other popups
        el.classList.remove('mapboxgl-popup-front');
        el.classList.remove('mapboxgl-popup-highlight');
      });
    },
    closeMap() {
      this.$store.dispatch('ui/setMapDisplay', { mapDisplay: false });
    },
    fetchCommunities() {
      // apply taxonomy or meta queries
      const payload = this.setPayload();

      // get communities from WP
      return this.$store.dispatch('communities/all', payload);
    },
    fetchData() {
      this.fetchQuickMoveIns();
      this.fetchCommunities();
      this.fetchPointsOfInterest();
    },
    fetchPointsOfInterest() {
      // get points of interest from WP
      const payload = this.setPayload();

      this.$store
        .dispatch('pointsOfInterest/all', payload)
        .then((response) => {
          this.pointsOfInterest = response.data.posts;
        })
        .catch()
        .finally();
    },
    fetchQuickMoveIns() {
      // apply taxonomy or meta queries
      const payload = this.setPayload();

      // get Quick Move Ins from WP
      return this.$store.dispatch('quickMoveIns/all', payload);
    },
    getQuickMoveInById(id) {
      return this.quickMoveIns.find((q) => q.id === Number(id));
    },
    getQuickMoveInBySlug(slug) {
      return this.quickMoveIns.find((q) => q.slug === slug);
    },
    setupPopupObserver() {
      const mapContainer = this.map.getContainer();

      this.popupObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === 1 && node.classList.contains('mapboxgl-popup')) {
              this.attachPopupListeners(node);
            }
          });
        });
      });

      this.popupObserver.observe(mapContainer, { childList: true, subtree: true });

      // Handle any popups Mapbox may have already added to the container
      mapContainer.querySelectorAll('.mapboxgl-popup').forEach((el) => this.attachPopupListeners(el));
    },
    attachPopupListeners(element) {
      // Skip if this exact DOM element already has listeners attached
      if (element.dataset.listenersRegistered) return;

      // Only handle our quick move-in popups (ignore POI popups)
      const idClass = Array.from(element.classList).find((c) => c.startsWith('mapbox-popup-'));
      if (!idClass) return;

      const quickMoveInId = idClass.replace('mapbox-popup-', '');
      const popups = document.getElementsByClassName('mapboxgl-popup');

      element.dataset.listenersRegistered = 'true';

      /**
       * When we hover over the popup, bring it to the front
       */
      element.addEventListener('mouseover', () => {
        // console.log('QuickMoveInsMap attachPopupListeners() on mouseover', quickMoveInId);
        Array.from(popups).forEach((el) => el.classList.remove('mapboxgl-popup-front'));
        element.classList.add('mapboxgl-popup-front');
      });

      /**
       * Clicking the popup expands the popup and collapses the others. Then set the current quick move-in as the detail
       */
      element.addEventListener('click', () => {
        // console.log('QuickMoveInsMap attachPopupListeners() on click', quickMoveInId);
        const payload = { quickMoveIn: this.getQuickMoveInById(quickMoveInId), panelView: 'detail' };
        this.$store.dispatch('ui/setPanelView', payload);
      });
    },
    setPayload(extra = {}) {
      const payload = {
        tax_queries: [
          {
            taxonomy: 'community-area',
            field: 'slug',
            terms: this.city,
          },
        ],
      };

      return Object.assign(payload, extra);
    },
    showPopupDetail() {
      // console.log('QuickMoveInsMap showPopupDetail()', this.isMapDisplayed, this.community);
      this.clearPopups();

      const element = document
        .getElementsByClassName(`mapbox-popup-${this.quickMoveIn.id}`)[0]
        .closest('.mapboxgl-popup');

      // expand popup
      element.classList.add('mapboxgl-popup-front');
      element.classList.add('mapboxgl-popup-highlight');
    },
    togglePoi(toggle) {
      console.log('QuickMoveInsMap togglePoi()', toggle);
      // toggle display of POI layer
      this.showPointsOfInterest = toggle.value;
    },
    updateQuickMoveInsCoordinates() {
      // console.log('QuickMoveInsMap updateQuickMoveInsCoordinates()');
      this.quickMoveIns.forEach((quickMoveIn, i) => {
        // console.log('QuickMoveInsMap updateQuickMoveInsCoordinates() each');
        if (_.isEmpty(quickMoveIn.full_address)) return;
        this.mapboxGeoCodingClient
          .forwardGeocode({
            query: quickMoveIn.full_address,
            // limit: 2,
            autocomplete: false,
          })
          .send()
          .then((response) => {
            // console.log('QuickMoveInsMap updateQuickMoveInsCoordinates() each then', response.body);
            const payload = {
              index: i,
              field: 'coordinates',
              value: response.body.features.find((f) => f.place_type.includes('address')).geometry.coordinates,
            };
            this.$store.dispatch('quickMoveIns/setQuickMoveInAttribute', payload);
          });
      });
    },
  },
};
</script>
